/**
 * Company API Service (SESSION_004)
 *
 * Handles HTTP requests to the admin company management endpoints.
 * Backend endpoints created in SESSION_003 (Sprint 014 Phase 3).
 */

import { apiClient } from './ApiClient';
import type {
  GetBusinessCompanySuggestionsResponse,
  GetCompaniesByRegionResponse,
  GetCompanyByIdResponse,
  CreateBusinessCompanyCommand,
  CreateBusinessCompanyResponse,
  UpdateCompanyCommand,
  UpdateCompanyResponse,
  DeleteCompanyResponse,
  AddBusinessToCompanyResponse,
  RemoveBusinessFromCompanyResponse,
  BulkCreateCompaniesFromSuggestionsCommand,
  BulkCreateCompaniesResponse,
  CreateBusinessForCompanyCommand,
  CreateBusinessForCompanyResponse,
  BulkReassignBusinessesRequest,
  BulkReassignBusinessesResponse
} from '../../domain/entities/Company';

/**
 * Sprint 054: Response from setting no-public-email flag
 */
export interface SetNoPublicEmailResponse {
  success: boolean;
  message: string;
}

/**
 * Query parameters for company suggestions API
 */
export interface GetCompanySuggestionsParams {
  minGroupSize?: number; // Minimum businesses per group (default: 3 from backend)
  confidenceThreshold?: number; // Minimum confidence (0-100, default: 80 from backend)
}

/**
 * Query parameters for companies by region API
 */
export interface GetCompaniesByRegionParams {
  page?: number; // Page number (1-based)
  pageSize?: number; // Results per page (default: 20 from backend)
  searchTerm?: string; // Search by company name
}

/**
 * Query parameters for company by ID API
 */
export interface GetCompanyByIdParams {
  businessPage?: number; // Page number for businesses (1-based)
  businessPageSize?: number; // Results per page for businesses (default: 20 from backend)
}

/**
 * API service for admin company management
 */
export class CompanyApi {
  /**
   * Get company suggestions based on business similarity
   * Endpoint: GET /api/admin/companies/suggestions
   *
   * Returns auto-detected company suggestions using name similarity algorithm
   * @param regionId - Region ID to analyze for companies
   * @param params - Optional query parameters
   */
  async getCompanySuggestions(
    regionId: string,
    params?: GetCompanySuggestionsParams
  ): Promise<GetBusinessCompanySuggestionsResponse> {
    return apiClient.get<GetBusinessCompanySuggestionsResponse>(
      '/api/admin/companies/suggestions',
      {
        params: {
          regionId,
          ...params
        }
      }
    );
  }

  /**
   * Get all companies for a specific region (paginated)
   * Endpoint: GET /api/admin/companies/region/{regionId}
   *
   * Returns paginated list of companies with business counts
   * @param regionId - Region ID to get companies for
   * @param params - Pagination and search parameters
   */
  async getCompaniesByRegion(
    regionId: string,
    params?: GetCompaniesByRegionParams
  ): Promise<GetCompaniesByRegionResponse> {
    return apiClient.get<GetCompaniesByRegionResponse>(
      `/api/admin/companies`,
      {
        params: {
          ...params,
          regionId  // Backend expects regionId as query parameter, not in path
        }
      }
    );
  }

  /**
   * Get detailed company information by ID
   * Endpoint: GET /api/admin/companies/{id}
   *
   * Returns company details with paginated list of member businesses
   * @param id - Company ID
   * @param params - Pagination parameters for businesses
   */
  async getCompanyById(
    id: string,
    params?: GetCompanyByIdParams,
    options?: { showErrorToast?: boolean }
  ): Promise<GetCompanyByIdResponse> {
    return apiClient.get<GetCompanyByIdResponse>(`/api/admin/companies/${id}`, {
      params,
      showErrorToast: options?.showErrorToast ?? true
    });
  }

  /**
   * Create a new business company
   * Endpoint: POST /api/admin/companies
   *
   * Creates a new company (without businesses initially)
   * @param command - Company creation command
   */
  async createCompany(command: CreateBusinessCompanyCommand): Promise<CreateBusinessCompanyResponse> {
    return apiClient.post<CreateBusinessCompanyResponse>('/api/admin/companies', command);
  }

  /**
   * Bulk create companies from suggestions
   * Endpoint: POST /api/admin/companies/bulk
   *
   * Creates multiple companies with business associations from auto-detected suggestions
   * @param command - Bulk creation command with multiple suggestions
   */
  async bulkCreateCompanies(
    command: BulkCreateCompaniesFromSuggestionsCommand
  ): Promise<BulkCreateCompaniesResponse> {
    return apiClient.post<BulkCreateCompaniesResponse>(
      '/api/admin/companies/bulk',
      command
    );
  }

  /**
   * Update an existing company
   * Endpoint: PUT /api/admin/companies/{id}
   *
   * Updates company name, description, or active status
   * @param id - Company ID
   * @param command - Update command
   */
  async updateCompany(
    id: string,
    command: UpdateCompanyCommand
  ): Promise<UpdateCompanyResponse> {
    return apiClient.put<UpdateCompanyResponse>(`/api/admin/companies/${id}`, command);
  }

  /**
   * Delete a company (soft delete)
   * Endpoint: DELETE /api/admin/companies/{id}
   *
   * Soft deletes a company and all business associations
   * @param id - Company ID to delete
   */
  async deleteCompany(id: string): Promise<DeleteCompanyResponse> {
    return apiClient.delete<DeleteCompanyResponse>(`/api/admin/companies/${id}`);
  }

  /**
   * Add a business to a company
   * Endpoint: POST /api/admin/companies/{companyId}/businesses/{businessId}
   *
   * Associates a business with a company
   * @param companyId - Company ID
   * @param businessId - Business ID to add
   */
  async addBusinessToCompany(
    companyId: string,
    businessId: string
  ): Promise<AddBusinessToCompanyResponse> {
    return apiClient.post<AddBusinessToCompanyResponse>(
      `/api/admin/companies/${companyId}/businesses/${businessId}`
    );
  }

  /**
   * Create a new business and add it to a company
   * Endpoint: POST /api/admin/companies/{companyId}/businesses
   *
   * Creates a new business (Place, Amenity, Business, Alias chain) and associates it with the company
   * @param companyId - Company ID
   * @param command - Business creation fields
   */
  async createBusinessForCompany(
    companyId: string,
    command: Omit<CreateBusinessForCompanyCommand, 'companyId'>
  ): Promise<CreateBusinessForCompanyResponse> {
    return apiClient.post<CreateBusinessForCompanyResponse>(
      `/api/admin/companies/${companyId}/businesses`,
      command
    );
  }

  /**
   * Remove a business from a company
   * Endpoint: DELETE /api/admin/companies/{companyId}/businesses/{businessId}
   *
   * Disassociates a business from a company
   * @param companyId - Company ID
   * @param businessId - Business ID to remove
   */
  async removeBusinessFromCompany(
    companyId: string,
    businessId: string
  ): Promise<RemoveBusinessFromCompanyResponse> {
    return apiClient.delete<RemoveBusinessFromCompanyResponse>(
      `/api/admin/companies/${companyId}/businesses/${businessId}`
    );
  }

  /**
   * Set no-public-email-available flag on a company
   * Endpoint: PATCH /api/admin/companies/{companyId}/no-email-flag
   *
   * Sprint 054: Allows admin to mark a company as having no public email available.
   * @param companyId - Company ID
   * @param noPublicEmailAvailable - Whether to set or clear the flag
   */
  async setNoPublicEmailAvailable(
    companyId: string,
    noPublicEmailAvailable: boolean
  ): Promise<SetNoPublicEmailResponse> {
    return apiClient.patch<SetNoPublicEmailResponse>(
      `/api/admin/companies/${companyId}/no-email-flag`,
      { noPublicEmailAvailable }
    );
  }

  /**
   * Bulk reassign businesses to a target company
   * Endpoint: POST /api/admin/companies/{companyId}/bulk-reassign
   *
   * Reassigns multiple businesses to a target company, optionally deleting orphaned source companies.
   * Sprint 053: Admin business reassignment for consolidating chain locations.
   * @param targetCompanyId - Target company ID to reassign businesses to
   * @param request - Business IDs and orphan deletion flag
   */
  async bulkReassignBusinesses(
    targetCompanyId: string,
    request: BulkReassignBusinessesRequest
  ): Promise<BulkReassignBusinessesResponse> {
    return apiClient.post<BulkReassignBusinessesResponse>(
      `/api/admin/companies/${targetCompanyId}/bulk-reassign`,
      request
    );
  }
}

// Export singleton instance
export const companyApi = new CompanyApi();
